import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CareScenarioSummary } from '@shared/consumerPlan';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { useCareWalkthrough } from '../hooks/use-care-walkthrough';
import { CareCostBreakdown } from './care-cost-breakdown';
import { WalkthroughMoment } from './walkthrough-moment';

function MonthByMonth({ clientId }: { clientId: string }) {
  const { moment, moments, position, isLastMoment, showMoment, showNextMoment, showPreviousMoment } =
    useCareWalkthrough(clientId);
  if (!moment) return null;

  return (
    <div className="flex flex-col gap-5">
      <WalkthroughMoment
        moment={moment}
        framed={false}
        moments={moments}
        position={position}
        onSelectMoment={showMoment}
      />
      <p className="text-muted-foreground text-sm">Family help is already reflected in the bill.</p>
      <div className="flex flex-wrap gap-2">
        {position > 0 && (
          <Button variant="outline" size="sm" onClick={showPreviousMoment}>
            Previous moment
          </Button>
        )}
        {!isLastMoment && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={showNextMoment}>
            Next moment
            <ChevronRight />
          </Button>
        )}
      </div>
    </div>
  );
}

export function WalkthroughExploreSection({
  clientId,
  scenario,
}: {
  clientId: string;
  scenario: CareScenarioSummary;
}) {
  return (
    <Collapsible defaultOpen className="group/explore rounded-lg border bg-white">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between pb-3">
          Explore this scenario
          <ChevronDown className="transition-transform duration-200 ease-out group-data-[state=open]/explore:rotate-180" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-4 px-4 pt-1 pb-4">
        <Tabs defaultValue="walkthrough">
          <TabsList aria-label="Explore this scenario" className="h-auto min-h-10 w-full flex-wrap">
            <TabsTrigger value="walkthrough" className="whitespace-normal">
              Care Walkthrough
            </TabsTrigger>
            <TabsTrigger value="breakdown" className="whitespace-normal">
              Cost breakdown
            </TabsTrigger>
          </TabsList>
          <TabsContent value="walkthrough">
            <MonthByMonth clientId={clientId} />
          </TabsContent>
          <TabsContent value="breakdown">
            <CareCostBreakdown scenario={scenario} />
          </TabsContent>
        </Tabs>
      </CollapsibleContent>
    </Collapsible>
  );
}
